class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();

    // When page is restored from bfcache (back/forward navigation)
    window.addEventListener('pageshow', (event) => {
      if (!event.persisted) return;

      // Instantly hide cart drawer if it was open
      if (this.classList.contains('active')) {
        this.style.transition = 'none';
        this.classList.remove('active', 'animate');
        document.body.classList.remove('overflow-hidden');
        removeTrapFocus(this.activeElement);
        requestAnimationFrame(() => {
          this.style.transition = '';
        });
      }

      // Refresh cart icon bubble to reflect current cart state
      const iconBubble = document.getElementById('cart-icon-bubble');
      const bubble = iconBubble?.querySelector('.cart-count-bubble');
      if (bubble) bubble.style.visibility = 'hidden';

      this.scheduleRefresh();

      fetch(`${routes.cart_url}.js`)
        .then((res) => (CartThrottle.checkRefresh(res) ? null : res.json()))
        .then((cart) => {
          if (!cart || !iconBubble) {
            if (bubble) bubble.style.visibility = '';
            return;
          }
          const span = iconBubble.querySelector('.cart-count-bubble span[aria-hidden]');
          if (span) {
            span.textContent = cart.item_count;
          } else if (cart.item_count > 0) {
            const div = document.createElement('div');
            div.className = 'cart-count-bubble';
            div.innerHTML = `<span aria-hidden="true">${cart.item_count}</span>`;
            iconBubble.appendChild(div);
          }
          const updatedBubble = iconBubble.querySelector('.cart-count-bubble');
          if (updatedBubble) updatedBubble.style.visibility = '';
        })
        .catch(() => {
          if (bubble) bubble.style.visibility = '';
        });
    });
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    // A stale drawer cannot be shown while cart traffic is suspended; recover instead of opening
    if (this.stale && CartThrottle.blocked()) return;
    if (this.stale) this.refreshFromServer();
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
    document.documentElement.style.setProperty('--scrollbar-width', `${scrollbarWidth}px`);
    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
    document.documentElement.style.removeProperty('--scrollbar-width');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  // Sections bundled into Cart API responses (/cart/add, /cart/change) are not
  // guaranteed to carry the customer session, so anything gated on `customer`
  // (checkout CTA, hidden pricing) can render in the guest state. The drawer is
  // therefore always re-rendered from a plain GET of its section instead.
  refreshFromServer() {
    if (this.refreshPromise) {
      if (this.refreshStarted) this.refreshQueued = true;
      return this.refreshPromise;
    }

    if (CartThrottle.suspended) {
      this.stale = true;
      return Promise.resolve();
    }

    this.stale = false;
    this.refreshStarted = false;
    this.refreshPromise = Promise.resolve()
      .then(() => {
        this.refreshStarted = true;
        return fetch(`${routes.cart_url}?section_id=cart-drawer`);
      })
      .then((response) => {
        if (CartThrottle.checkRefresh(response)) {
          this.stale = true;
          if (this.classList.contains('active')) CartThrottle.recover();
          return '';
        }
        return response.ok ? response.text() : '';
      })
      .then((responseText) => this.replaceDrawerInner(responseText))
      .catch((e) => console.error(e))
      .finally(() => {
        this.refreshPromise = null;
        if (this.refreshQueued) {
          this.refreshQueued = false;
          return this.refreshFromServer();
        }
      });

    return this.refreshPromise;
  }

  // A mutation made while the drawer is closed does not need an immediate re-render.
  scheduleRefresh(itemCount) {
    const isOpen = this.classList.contains('active');
    const isEmpty = this.classList.contains('is-empty') || !!this.querySelector('.drawer__inner-empty');
    const willBeEmpty = typeof itemCount === 'number' && itemCount === 0;
    if (isOpen || isEmpty || willBeEmpty) return this.refreshFromServer();

    this.stale = true;
    return Promise.resolve();
  }

  replaceDrawerInner(responseText) {
    if (!responseText) return;
    const html = new DOMParser().parseFromString(responseText, 'text/html');
    const sourceDrawer = html.querySelector('cart-drawer');
    const sourceInner = html.querySelector('.drawer__inner');
    const targetInner = this.querySelector('.drawer__inner');
    if (!sourceInner || !targetInner) return;

    const active = document.activeElement;
    const focusItem = active && this.contains(active) ? active.closest('[id^="CartDrawer-Item-"]') : null;
    const focusName = focusItem ? active.getAttribute('name') : null;

    targetInner.innerHTML = sourceInner.innerHTML;
    if (sourceDrawer) this.classList.toggle('is-empty', sourceDrawer.classList.contains('is-empty'));

    if (!this.classList.contains('active')) return;
    const container =
      (this.classList.contains('is-empty') && this.querySelector('.drawer__inner-empty')) ||
      document.getElementById('CartDrawer');
    const focusTarget =
      (focusName && this.querySelector(`#${focusItem.id} [name="${focusName}"]`)) ||
      this.querySelector('.drawer__inner');
    trapFocus(container, focusTarget);
  }

  renderContents(parsedState) {
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement || !parsedState.sections || !parsedState.sections[section.id]) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    this.refreshFromServer().then(() => this.open());
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  connectedCallback() {
    this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      if (event.isError && !event.cartData) return;
      // product-info publishes before its own request completes; it refreshes the drawer itself afterwards
      if (event.source === 'product-info-sync') return;
      return this.onCartUpdate(event);
    });
  }

  onCartUpdate(event) {
    const drawer = this.closest('cart-drawer') || document.querySelector('cart-drawer');
    return drawer ? drawer.scheduleRefresh(event?.cartData?.item_count) : Promise.resolve();
  }

  // The drawer's empty markup exists only in a server render, so the class that
  // reveals it is applied by refreshFromServer(), not ahead of the repaint.
  toggleEmptyState() {}

  getSectionsToRender() {
    return [
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
